/**
 * practiceDate — the frontend's "today" in the PRACTICE's calendar.
 *
 * A recorded date (the day a medication list was made, the day a step was
 * done) is anchored to the practice's local calendar day, not the server's
 * UTC day and not each viewer's device day, so a form's pre-filled default
 * and the server's not-after-today ceiling agree at every wall-clock
 * moment. The platform pins America/New_York; a non-ET tenant threads its
 * zone through the optional parameter.
 *
 * MIRROR: api/_shared/businessDays.ts (practiceTodayIso) is the server twin;
 * src/components/workspace/workspaceFormat.ts (todayStr) calls this one.
 */

export const PRACTICE_TIME_ZONE = 'America/New_York';

/** Today as YYYY-MM-DD in the practice's calendar. Intl does the DST-correct
 *  zone math; assembled from formatToParts by part type so the locale tag
 *  never shapes the output. */
export function practiceTodayIso(now: Date = new Date(), timeZone: string = PRACTICE_TIME_ZONE): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: 'year' | 'month' | 'day'): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** The day a new note's date box opens on: the device's calendar day, but
 *  never later than the practice's. West of the practice (or in its zone)
 *  that is the device's day, so an evening visit keeps its own date; east of
 *  it, after the practice's midnight, it is the practice's day, so the box
 *  never opens above its max. */
export function noteDateDefault(now: Date = new Date(), timeZone: string = PRACTICE_TIME_ZONE): string {
  const device = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const practice = practiceTodayIso(now, timeZone);
  return device < practice ? device : practice;
}

/** The practice's calendar day of an ISO-8601 instant (a full date and time
 *  ending in Z or an offset, the shape a server timestamp travels in), or
 *  null when the text is not one. */
export function practiceDayOf(instant: string, timeZone: string = PRACTICE_TIME_ZONE): string | null {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/.test(instant)) return null;
  const at = new Date(instant);
  return Number.isNaN(at.getTime()) ? null : practiceTodayIso(at, timeZone);
}

/** The practice's calendar day of a stored value, for comparing with
 *  practiceTodayIso: a date-only YYYY-MM-DD is already a calendar day and
 *  passes through; any other text the platform parses as an instant resolves
 *  to its day in the practice's zone; anything else is null. A surface that
 *  reads "today" from practiceTodayIso reads its stamps' days here, never
 *  from the device's zone, or the two disagree for hours each night. */
export function practiceDayOfStamp(value: string, timeZone: string = PRACTICE_TIME_ZONE): string | null {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : practiceTodayIso(at, timeZone);
}
